/** QuickPlan — Plan de perçage d'une face : liste de primitives (unités : mm papier,
 *  y vers le bas) dessinée à l'identique en SVG (écran) et avec jsPDF (export).
 *
 *  Cotation en coordonnées depuis le coin bas-gauche de la face vue de l'extérieur :
 *  abscisses sous la face, ordonnées à gauche, étagées quand elles se chevauchent.
 *  Trou traversant = cercle + croix ; trou borgne = disque gris ; encoche = rectangle hachuré.
 */

const fmt = (v) => String(Math.round(v * 10) / 10).replace('.', ',');
const TXT = 2.6; // hauteur de texte des cotes (mm)
const textW = (s, size = TXT) => s.length * size * 0.56;

/**
 * face : { size: [W, H], holes: [{ u, v, dia, through }] }
 * maxW, maxH : place disponible (mm). Rend { w, h, items, scale, stretched }.
 */
export function faceDrawing(face, maxW, maxH) {
  const [W, H] = face.size;
  const left = 24; // place des cotes verticales (deux colonnes)
  const bottomRows = 3;
  const bottom = 6 + bottomRows * (TXT + 1.6);
  const top = 3;
  const availW = maxW - left - 4;
  const availH = maxH - bottom - top;
  let s = Math.min(availW / W, availH / H);
  const dw = W * s;
  let dh = H * s;
  // Chant très fin : on épaissit la vue (cotes justes, aspect dilaté).
  const stretched = dh < 9;
  if (stretched) dh = Math.min(9, availH);
  const sv = dh / H;
  const x0 = left;
  const y0 = top;
  const X = (u) => x0 + u * s;
  const Y = (v) => y0 + dh - v * sv;
  const items = [];

  items.push({ t: 'rect', x: x0, y: y0, w: dw, h: dh, stroke: '#111', lw: 0.35, fill: '#f6f3ee' });

  // Encoches (logements) : rectangle gris clair hachuré, au moins visibles.
  for (const r of face.recesses ?? []) {
    const rx = X(r.u);
    const rw = Math.max(0.8, r.w * s);
    const ry = Y(r.v + r.h);
    const rh = Math.max(0.8, r.h * sv);
    items.push({ t: 'rect', x: rx, y: ry, w: rw, h: rh, stroke: '#111', lw: 0.25, fill: '#c9d3dd' });
    const step = 1.2;
    for (let d = step; d < rw + rh; d += step) {
      const x1 = rx + Math.max(0, d - rh);
      const y1 = ry + Math.min(rh, d);
      const x2 = rx + Math.min(rw, d);
      const y2 = ry + Math.max(0, d - rw);
      items.push({ t: 'line', x1, y1, x2, y2, lw: 0.08, color: '#556' });
    }
  }

  // Trous
  for (const h of face.holes) {
    const cx = X(h.u);
    const cy = Y(h.v);
    const r = Math.max(0.9, Math.min((h.dia / 2) * s, 4));
    if (h.through) {
      items.push({ t: 'circle', cx, cy, r, stroke: '#111', lw: 0.3, fill: '#fff' });
      items.push({ t: 'line', x1: cx - r * 0.75, y1: cy - r * 0.75, x2: cx + r * 0.75, y2: cy + r * 0.75, lw: 0.22 });
      items.push({ t: 'line', x1: cx - r * 0.75, y1: cy + r * 0.75, x2: cx + r * 0.75, y2: cy - r * 0.75, lw: 0.22 });
    } else {
      items.push({ t: 'circle', cx, cy, r, stroke: '#111', lw: 0.3, fill: '#8a8f96' });
    }
  }

  // Cotes horizontales (abscisses), étagées sur plusieurs rangs si trop serrées.
  const rec = face.recesses ?? [];
  const us = [...new Set([0, ...face.holes.map((h) => h.u), ...rec.flatMap((r) => [r.u, r.u + r.w]), W].map((v) => Math.round(v * 10) / 10))].sort((a, b) => a - b);
  const rowsEnd = Array(bottomRows).fill(-Infinity);
  const baseY = y0 + dh;
  for (const u of us) {
    const label = fmt(u);
    const x = X(u);
    const tw = textW(label);
    let row = rowsEnd.findIndex((end) => x - tw / 2 > end + 0.8);
    if (row < 0) continue; // trop serré : la valeur reste dans le tableau
    rowsEnd[row] = x + tw / 2;
    const ty = baseY + 4 + row * (TXT + 1.6) + TXT;
    items.push({ t: 'line', x1: x, y1: baseY + 0.6, x2: x, y2: ty - TXT - 0.3, lw: 0.15, color: '#666' });
    items.push({ t: 'text', x, y: ty, s: label, size: TXT, anchor: 'middle' });
  }

  // Cotes verticales (ordonnées), décalées vers la gauche si serrées.
  const vs = [...new Set([0, ...face.holes.map((h) => h.v), ...rec.flatMap((r) => [r.v, r.v + r.h]), H].map((v) => Math.round(v * 10) / 10))].sort((a, b) => a - b);
  const cols = [Infinity, Infinity]; // dernier y occupé par colonne (on remonte)
  for (const v of vs) {
    const y = Y(v);
    let col = cols.findIndex((last) => last - y > TXT + 0.4 || last === Infinity);
    if (col < 0) continue;
    cols[col] = y;
    const tx = x0 - 1.2 - col * 10.5;
    items.push({ t: 'line', x1: x0 - 0.6, y1: y, x2: tx + 0.3, y2: y, lw: 0.15, color: '#666' });
    items.push({ t: 'text', x: tx, y: y + TXT * 0.35, s: fmt(v), size: TXT, anchor: 'end' });
  }

  // Lignes d'axe des trous jusqu'aux bords (fines, pointillées).
  for (const h of face.holes) {
    items.push({ t: 'line', x1: X(h.u), y1: Y(h.v), x2: X(h.u), y2: baseY, lw: 0.1, color: '#9aa', dash: [0.8, 0.8] });
    items.push({ t: 'line', x1: x0, y1: Y(h.v), x2: X(h.u), y2: Y(h.v), lw: 0.1, color: '#9aa', dash: [0.8, 0.8] });
  }

  return { w: left + dw + 4, h: top + dh + bottom, items, scale: s, stretched };
}

/** Désignation courte d'un trou : « Ø5 prof. 31 » / « Ø7 traversant ». */
export function holeText(h) {
  return `Ø${fmt(h.dia)} ${h.through ? 'traversant' : `prof. ${fmt(h.depth)}`}`;
}

/** Désignation d'une encoche : « encoche 13,5 × 9 prof. 3, débouchante chant gauche ». */
export function recessText(r) {
  return `${r.name ? `${r.name} ` : 'encoche '}${fmt(r.w)} × ${fmt(r.h)} prof. ${fmt(r.depth)}${r.open?.length ? `, débouchante ${r.open.map((o) => o.toLowerCase()).join(' et ')}` : ''}`;
}

/** Encoches identiques regroupées : [{ ...r, count }]. */
export function recessSummary(list) {
  const map = new Map();
  for (const r of list ?? []) {
    const k = recessText(r);
    const row = map.get(k) ?? { ...r, count: 0 };
    row.count++;
    map.set(k, row);
  }
  return [...map.values()];
}

/** Trous identiques regroupés pour le tableau : [{ dia, through, depth, count, list }]. */
export function holeSummary(holes) {
  const map = new Map();
  for (const h of holes) {
    const k = `${h.dia}|${h.through}|${h.through ? '' : h.depth}`;
    const row = map.get(k) ?? { dia: h.dia, through: h.through, depth: h.depth, count: 0, list: [] };
    row.count++;
    row.list.push(h);
    map.set(k, row);
  }
  return [...map.values()];
}

export { fmt };

/**
 * Plan de coupe d'un panneau : pièces repérées (lettre + cotes), chutes réutilisables,
 * zone de rognage en pointillé, cotes hors tout. Longueur du panneau à l'horizontale.
 * sheet : { placements, offcuts }, format : { length, width }, trim (mm).
 */
export function sheetDrawing(sheet, format, trim, maxW, maxH) {
  const left = 9;
  const top = 6;
  const s = Math.min((maxW - left - 2) / format.length, (maxH - top - 2) / format.width);
  const X = (x) => left + x * s;
  const Y = (y) => top + y * s;
  const items = [];
  items.push({ t: 'rect', x: X(0), y: Y(0), w: format.length * s, h: format.width * s, stroke: '#111', lw: 0.35, fill: '#f4f4f2' });
  if (trim > 0) {
    items.push({ t: 'rect', x: X(trim), y: Y(trim), w: (format.length - 2 * trim) * s, h: (format.width - 2 * trim) * s, stroke: '#999', lw: 0.15, dash: [0.8, 0.8] });
  }
  items.push({ t: 'text', x: X(format.length / 2), y: top - 1.6, s: fmt(format.length), size: 2.6, anchor: 'middle' });
  items.push({ t: 'text', x: left - 1.6, y: Y(format.width / 2), s: fmt(format.width), size: 2.6, anchor: 'middle', rot: 90 });

  for (const o of sheet.offcuts ?? []) {
    const w = o.w * s;
    const h = o.h * s;
    items.push({ t: 'rect', x: X(o.x), y: Y(o.y), w, h, stroke: '#bbb', lw: 0.12, fill: '#ececea', dash: [0.6, 0.6] });
    const label = `chute ${fmt(o.w)} × ${fmt(o.h)}`;
    if (textW(label, 2) < w - 2 && h > 4) items.push({ t: 'text', x: X(o.x + o.w / 2), y: Y(o.y + o.h / 2) + 0.7, s: label, size: 2, anchor: 'middle', color: '#888' });
  }

  for (const p of sheet.placements) {
    const w = p.w * s;
    const h = p.h * s;
    const cx = X(p.x + p.w / 2);
    const cy = Y(p.y + p.h / 2);
    items.push({ t: 'rect', x: X(p.x), y: Y(p.y), w, h, stroke: '#111', lw: 0.3, fill: '#e9dcc3' });
    // Cotes de la pièce dans l'ordre de la liste de débit (longueur × largeur).
    const dims = `${fmt(p.L)} × ${fmt(p.W)}`;
    const L = Math.min(4, Math.max(2, Math.min(w, h) * 0.32));
    const D = Math.min(2.6, L * 0.75);
    const vertical = h > w * 1.6 && textW(dims, D) > w - 1.5;
    const span = vertical ? h : w;
    const room = vertical ? w : h;
    const both = textW(dims, D) < span - 1.5 && room > L + D + 1.5;
    const letter = p.letter || '·';
    if (vertical) {
      if (both) {
        items.push({ t: 'text', x: cx - D * 0.5, y: cy, s: letter, size: L, anchor: 'middle', bold: true, rot: 90 });
        items.push({ t: 'text', x: cx + L * 0.55, y: cy, s: dims, size: D, anchor: 'middle', rot: 90 });
      } else if (room > L * 0.8) items.push({ t: 'text', x: cx + L * 0.35, y: cy, s: letter, size: L, anchor: 'middle', bold: true, rot: 90 });
    } else if (both) {
      items.push({ t: 'text', x: cx, y: cy - 0.2, s: letter, size: L, anchor: 'middle', bold: true });
      items.push({ t: 'text', x: cx, y: cy + D + 0.6, s: dims, size: D, anchor: 'middle' });
    } else if (h > L * 0.8) {
      items.push({ t: 'text', x: cx, y: cy + L * 0.35, s: letter, size: L, anchor: 'middle', bold: true });
    }
  }
  return { w: left + format.length * s + 2, h: top + format.width * s + 2, items, scale: s };
}
